import React, { useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import OrganizationSelector from './OrganizationSelector';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useOrganization } from '../contexts/OrganizationContext';
import SkipLinks from './SkipLinks';

const Layout: React.FC = () => {
  const { theme } = useTheme();
  const { isAuthenticated, loading } = useAuth();
  const { currentOrganization, userOrganizations, loading: orgLoading } = useOrganization();
  const navigate = useNavigate();
  
  useEffect(() => {
    console.log('🏠 Layout useEffect triggered:', { isAuthenticated, loading });
    if (!loading && !isAuthenticated) {
      console.log('🔄 Layout: Redirecting to /auth');
      navigate('/auth');
    } else if (!loading && isAuthenticated) {
      console.log('✅ Layout: User authenticated, staying on current page');
    }
  }, [isAuthenticated, loading, navigate]);
  
  // Show loading if auth is loading OR orgs are loading OR stored org is being loaded
  const hasStoredOrg = localStorage.getItem('currentOrganizationId');
  const isLoading = loading || orgLoading || (hasStoredOrg && !currentOrganization);
  
  if (isLoading) {
    console.log('⏳ Layout showing loading state', { loading, orgLoading, hasStoredOrg: !!hasStoredOrg, hasCurrentOrg: !!currentOrganization });
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading application...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    console.log('❌ Layout: User not authenticated, returning null (should redirect)');
    return <div />;
  }

  // Show organization selector if:
  // 1. User has multiple orgs
  // 2. Organizations have finished loading
  // 3. No organization is currently selected in state
  // 4. AND showSelectorOnLoad is true (fresh login with multiple orgs)
  // IMPORTANT: If there's a stored org ID, we're already showing loading spinner above
  const { showSelectorOnLoad } = useOrganization();
  
  // Only show selector if:
  // - Organizations have finished loading
  // - Multiple orgs exist
  // - No current org in state yet
  // - AND showSelectorOnLoad is true (fresh login)
  const shouldShowSelector = !orgLoading &&
                            userOrganizations.length > 1 && 
                            !currentOrganization && 
                            !hasStoredOrg &&  // Don't show if stored org exists (it will load via loading spinner)
                            showSelectorOnLoad;  // Only show on fresh login
  
  if (shouldShowSelector) {
    console.log('🏢 Layout: Showing organization selector', { 
      hasCurrentOrg: !!currentOrganization, 
      showSelectorOnLoad,
      hasStoredOrg: !!hasStoredOrg,
      orgCount: userOrganizations.length,
      orgLoading
    });
    return <OrganizationSelector />;
  }
  
  console.log('🎨 Layout: Rendering main layout');
  return (
    <div className={`flex flex-col md:flex-row h-screen bg-gray-50 ${theme === 'dark' ? 'dark' : ''}`}>
      <SkipLinks />
      <Sidebar />
      <div className="flex flex-col flex-1 overflow-hidden order-first md:order-last">
        <Header />
        <main 
          id="main-content"
          className="flex-1 overflow-y-auto p-3 md:p-4 pb-20 md:pb-4 bg-gray-50 dark:bg-gray-900"
          tabIndex={-1}
          role="main"
          aria-label="Main content"
        >
          <div className="w-full mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;